import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { replaceInChain } from "@insite/client-framework/HandlerCreator";
import { createHandlerChainRunner, HandlerWithResult } from "@insite/client-framework/HandlerCreator";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUrl } from "@insite/client-framework/ServerSideRendering";
import { Session } from "@insite/client-framework/Services/SessionService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateById } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrand from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrand";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import {
    chain,
    DispatchSetStructuredPageData,
    PrepareStructuredPageData,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/GenerateStructuredPageData";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

// this will enable HMR for custom handlers
if (module.hot) {
    module.hot.accept();
    const originalChain = chain.slice();
    module.hot.dispose(data => {
        chain.length = 0;
        originalChain.forEach(o => chain.push(o));
    });
}

replaceInChain(chain, PrepareStructuredPageData, async props => {

    const {
        id,
        productTitle,
        smallImagePath,
        mediumImagePath,
        largeImagePath,
        canonicalUrl,
        productNumber,
        manufacturerItem,
        detail,
        brand: productBrand,
    } = props.parameter.product;
    const state = props.getState();

    const catalogPageState = getCatalogPageStateByPath(state, canonicalUrl);
    const description = catalogPageState.value?.metaDescription ?? productTitle;

    let productURL = "";
    const url: { protocol: string; host: string } = IS_SERVER_SIDE ? getUrl()! : window.location;
    productURL = `${url.protocol}//${url.host + canonicalUrl}`;

    const data = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: productTitle,
        description,
        image: [smallImagePath, mediumImagePath, largeImagePath],
        url: productURL,
        sku: productNumber,
        mpn: manufacturerItem,
        upc: detail?.upcCode,
    } as any;

    if (props.parameter.brandId) {
        let brandState = getBrandStateById(state, props.parameter.brandId);
        let brand = productBrand || brandState.value;
        if (!brand && !brandState.isLoading) {
            const awaitableLoadBrand = makeHandlerChainAwaitable(loadBrand);
            await awaitableLoadBrand({ brandId: props.parameter.brandId })(props.dispatch, props.getState);
            brandState = getBrandStateById(props.getState(), props.parameter.brandId);
            brand = brandState.value;
        }

        if (brand) {
            data.brand = {
                "@type": "Brand",
                identifier: brand.id,
                name: brand.name,
                url: brand.detailPagePath,
            };
        }
    }

    const productInfo = props.parameter.productInfo;

    if (
        productInfo &&
        !props.parameter.product.quoteRequired &&
        (!getIsWebCrawler() || getSettingsCollection(state).websiteSettings.includeRealTimeInventoryAndPricingForSeo)
    ) {

        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        const currentDate = new Date().getDate();
        const date = new Date(currentYear + 1, currentMonth, currentDate);

        await waitFor(() => !!productInfo.pricing && !!productInfo.inventory);
        const { pricing, inventory, unitOfMeasure } = productInfo;
        if (pricing && inventory && pricing.unitNetPrice > 0) {
            const availability = inventory?.inventoryAvailabilityDtos?.find(
                o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
            )?.availability;

            let availabilityData = "OutOfStock";
            const today = new Date();
            let estimatedDeliveryDate = "";
            const averageLeadTime = Number(props.parameter.product?.properties?.averageLeadTime);
            if (averageLeadTime > 0 && averageLeadTime <= 180 && availability?.messageType === 2) {
                availabilityData = "BackOrder";
                today.setDate(today.getDate() + averageLeadTime);

                let formatedMonth = today.getMonth() + 1;
                if (formatedMonth < 10) {
                    formatedMonth = 0 + formatedMonth;
                }
                estimatedDeliveryDate = (`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${(String(today.getDate())).padStart(2, '0')}`).toString();
            }

            if (availability?.messageType === 1 || availability?.messageType === 3 || inventory.qtyOnHand > 0) {
                availabilityData = "InStock";
            }
            data.offers = {
                "@type": "Offer",
                url: productURL,
                priceCurrency: props.parameter.session?.currency?.currencyCode,
                price: pricing.extendedUnitNetPrice,
                priceValidUntil: date.toLocaleString(),
                itemCondition: "https://schema.org/NewCondition",
                availability: `https://schema.org/${availabilityData}`,
                availabilityStarts: estimatedDeliveryDate
            };

            if (data.offers.availability != "https://schema.org/BackOrder") {
                delete data.offers["availabilityStarts"];
            }

        }
    }

    if (!data.offers) {
        props.result = { structuredPageData: "" };
        return;
    }

    // if (props.parameter.categoryIdWithBrandId || props.parameter.categoryId) {
    //     const category = getCategoryState(
    //         state,
    //         props.parameter.categoryIdWithBrandId || props.parameter.categoryId || "",
    //     ).value;

    //     if (category) {
    //         data.category = category.path;
    //     }
    // }

    props.result = { structuredPageData: JSON.stringify(data) };
});